import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { BrowserMultiFormatReader, BarcodeFormat } from "@zxing/browser";
import {
    Button,
    CircularProgress,
    Typography,
    Paper,
    Box,
    Divider,
    Switch,
    FormControlLabel,
    TextField,
    TableBody,
    TableHead,
    TableContainer,
    Table,
    TableCell,
    TableRow
} from "@mui/material"; // Updated to MUI v5
import Autocomplete from '@mui/material/Autocomplete'; // Updated import path
import HttpService from '../services/httpService';
import { useSelector } from "react-redux";
import { useDispatch } from 'react-redux';
import { setLatestScan } from "../redux/actions/leadSourceActions";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { ROLENAME } from "../utils";
// Constants to avoid magic strings
const SCAN_MODE = {
    MANUAL: "manual",
    AUTOMATED: "automated"
};

const STATUS_TYPE = {
    INFO: "info",
    SUCCESS: "success",
    WARNING: "warning",
    ERROR: "error"
};

const PERMISSION_STATUS = {
    UNKNOWN: "unknown",
    REQUESTING: "requesting",
    GRANTED: "granted",
    DENIED: "denied"
};

// Extracted styles to improve readability
const styles = {
    container: { padding: 4, maxWidth: 600, margin: "0 auto" },
    statusBar: (type) => ({
        marginBottom: 3,
        padding: 2,
        backgroundColor:
            type === STATUS_TYPE.ERROR ? "#ffebee" :
                type === STATUS_TYPE.SUCCESS ? "#e8f5e9" :
                    type === STATUS_TYPE.WARNING ? "#fff8e1" : "#e3f2fd",
        color:
            type === STATUS_TYPE.ERROR ? "#c62828" :
                type === STATUS_TYPE.SUCCESS ? "#2e7d32" :
                    type === STATUS_TYPE.WARNING ? "#f57f17" : "#1565c0",
        borderRadius: 1
    }),
    scanControls: { display: "flex", justifyContent: "space-between", marginBottom: 3 },
    scanButton: { width: '32%' },
    videoContainer: {
        width: "100%",
        height: 350,
        position: "relative",
        backgroundColor: "#000",
        borderRadius: 2,
        overflow: "hidden"
    },
    video: {
        width: "100%",
        height: "100%",
        objectFit: "cover"
    },
    scanInstruction: {
        position: "absolute",
        top: "10%",
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(0,0,0,0.7)",
        color: "white",
        padding: "8px 16px",
        borderRadius: 4,
        zIndex: 10
    },
    targetArea: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "70%",
        height: "70%",
        maxHeight: "280px",
        maxWidth: "280px",
        aspectRatio: "1/1",
        border: "2px solid rgba(255,0,0,0.5)",
        boxSizing: "border-box",
        pointerEvents: "none",
        borderRadius: 4
    },
    cornerMarker: {
        width: '30px',
        height: '30px',
        position: 'absolute',
        borderWidth: '6px',
        borderColor: 'rgba(0,255,0,0.8)'
    },
    topLeftCorner: {
        top: '-3px',
        left: '-3px',
        borderTop: '6px solid rgba(0,255,0,0.8)',
        borderLeft: '6px solid rgba(0,255,0,0.8)',
        borderTopLeftRadius: '4px'
    },
    topRightCorner: {
        top: '-3px',
        right: '-3px',
        borderTop: '6px solid rgba(0,255,0,0.8)',
        borderRight: '6px solid rgba(0,255,0,0.8)',
        borderTopRightRadius: '4px'
    },
    bottomLeftCorner: {
        bottom: '-3px',
        left: '-3px',
        borderBottom: '6px solid rgba(0,255,0,0.8)',
        borderLeft: '6px solid rgba(0,255,0,0.8)',
        borderBottomLeftRadius: '4px'
    },
    bottomRightCorner: {
        bottom: '-3px',
        right: '-3px',
        borderBottom: '6px solid rgba(0,255,0,0.8)',
        borderRight: '6px solid rgba(0,255,0,0.8)',
        borderBottomRightRadius: '4px'
    },
    scanLine: {
        position: "absolute",
        top: "50%",
        left: "50%",
        height: "2px",
        width: "70%",
        backgroundColor: "rgba(0,255,0,0.8)",
        zIndex: 2,
        transform: "translate(-50%, -50%)",
        maxWidth: "280px",
        animation: "scanLine 1.5s infinite ease-in-out"
    },
    sectionContainer: {
        marginBottom: 3,
        padding: 2,
        border: "1px solid #ccc",
        borderRadius: 4
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    permissionSection: {
        marginBottom: 3,
        paddingTop: 2,
        paddingBottom: 2,
        backgroundColor: "#f5f5f5",
        borderRadius: 1,
        textAlign: "center"
    },
    resultsContainer: { padding: 2 }
};

// KeyFrames for scan animation
const keyframes = `
  @keyframes scanLine {
    0% { top: 35%; }
    50% { top: 65%; }
    100% { top: 35%; }
  }
`;

const ScanPiece = () => {
    // State with default values
    const cameraLockedRef = useRef(false);
    const [state, setState] = useState({
        scanMode: SCAN_MODE.AUTOMATED,
        data: "Waiting for scan...",
        devices: [],
        selectedDeviceId: "",
        scanning: false,
        loading: false,
        lastScanned: "",
        permissionStatus: PERMISSION_STATUS.UNKNOWN,
        statusMessage: "",
        statusType: STATUS_TYPE.INFO,
        startButtonDisabled: false,
        stopButtonDisabled: true,
        continueButtonDisabled: true,
        projectNo: "",
        packageNum: "",
    });

    // Add dialog state to your component
    const [dialogOpen, setDialogOpen] = useState(false);
   
    const [dialogMessage, setDialogMessage] = useState('');
    const [onDialogConfirm, setOnDialogConfirm] = useState(() => () => { });


    const [completionCount, setCompletionCount] = useState([])
    const projectDetails = useSelector((state) => state.leadSource.projectNumber);

    const dispatch = useDispatch()

    // Destructure state for easier access
    const {
        scanMode, data, devices, selectedDeviceId, scanning, loading, lastScanned,
        permissionStatus, statusMessage, statusType, startButtonDisabled,
        stopButtonDisabled, continueButtonDisabled, projectNo, packageNum
    } = state;

    const [projectNumber, setProjectNo] = useState(() => {
        return JSON.parse(sessionStorage.getItem('projectNo'));
    });

    // Update state in a more efficient way
    const updateState = useCallback((newState) => {
        setState(prevState => ({ ...prevState, ...newState }));
    }, []);

    // Refs
    const videoRef = useRef(null);
    const codeReader = useRef(null);
    const streamRef = useRef(null);
    const statusTimeoutRef = useRef(null);

    // Memoized computed values for better performance
    const package_name = useMemo(() =>
        `${projectNo}_package_${packageNum}`,
        [projectNo, packageNum]
    );

    // Project options - memoized to prevent recreation on each render
    const [projectOptions, setManualProjectOption] = useState([])
    const { userDetails } = useSelector(state => state.user)

    useEffect(() => {
        getInTransitProjects()
        // eslint-disable-next-line
    }, [])

    useEffect(() => {

        calculateCompletionPiece(projectDetails);
        // eslint-disable-next-line
    }, [projectDetails])


    const getInTransitProjects = async () => {


        try {

            let result = null
            result = await HttpService.getInTransitProjects(userDetails.locationId)

            setManualProjectOption(result.data)

        } catch (error) {
            console.log(error, "error")

        }

    }


    // Function to show visual feedback on the screen - memoized with useCallback
    const showStatus = useCallback((message, type = STATUS_TYPE.INFO) => {
        updateState({ statusMessage: message, statusType: type });

        // Clear previous timeout if exists
        if (statusTimeoutRef.current) {
            clearTimeout(statusTimeoutRef.current);
        }

        // Auto-clear status after a delay
        statusTimeoutRef.current = setTimeout(() => {
            updateState({ statusMessage: "" });
        }, 8000);
    }, [updateState]);

    // Initialize code reader safely
    const initializeCodeReader = useCallback(() => {
        try {
            // Only create a new instance if one doesn't exist
            if (!codeReader.current) {
                codeReader.current = new BrowserMultiFormatReader();

                // Configure optimal settings for QR code scanning
                const formats = [
                    BarcodeFormat.QR_CODE,
                    BarcodeFormat.DATA_MATRIX
                ];

                const hints = new Map();
                hints.set(0, formats);  // 0 = POSSIBLE_FORMATS
                hints.set(2, true);     // 2 = TRY_HARDER

                codeReader.current.hints = hints;
            }
            return true;
        } catch (error) {
            console.error("Error initializing code reader:", error);
            return false;
        }
    }, []);

    // Function to safely reset the code reader
    const safelyResetCodeReader = useCallback(() => {
        try {
            if (codeReader.current && typeof codeReader.current.reset === 'function') {
                codeReader.current.reset();
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error resetting code reader:", error);
            // If reset fails, create a new one next time
            codeReader.current = null;
            return false;
        }
    }, []);

    // Handle camera stream cleanup
    const cleanupCameraStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    }, []);

    // Toggle between manual and automated scanning modes
    const handleToggleMode = useCallback(() => {
        // First stop scanning if active
        if (scanning) {
            updateState({ scanning: false });
            safelyResetCodeReader();
        }

        // Then toggle the mode
        updateState({
            scanMode: scanMode === SCAN_MODE.MANUAL ? SCAN_MODE.AUTOMATED : SCAN_MODE.MANUAL
        });
    }, [scanning, scanMode, updateState, safelyResetCodeReader]);

    // Request camera permission - improved for better reliability
    const requestCameraPermission = useCallback(async () => {
        try {
            updateState({ permissionStatus: PERMISSION_STATUS.REQUESTING });

            // Clean up any existing streams first
            cleanupCameraStream();

            // Wait until DOM is ready before accessing video ref
            await new Promise(resolve => setTimeout(resolve, 100));

            // Safety check for video element
            if (!videoRef.current) {
                console.warn("Video element not available yet");
            } else {
                // Clear any existing video source
                videoRef.current.srcObject = null;
            }

            // Use getUserMedia with minimal constraints
            const constraints = {
                audio: false,
                video: {
                    facingMode: 'environment', // Prefer back camera
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            // Store the stream for later cleanup
            streamRef.current = stream;

            // Make sure video element exists before attaching stream
            if (videoRef.current) {
                try {
                    // Set video attributes first
                    videoRef.current.setAttribute('playsinline', 'true'); // Important for iOS
                    videoRef.current.setAttribute('autoplay', 'true');
                    videoRef.current.muted = true;

                    // Then set the source
                    videoRef.current.srcObject = stream;

                    // Use a slight delay to avoid race conditions
                    setTimeout(() => {
                        if (videoRef.current) {
                            videoRef.current.play().catch(e => {
                                console.error("Error playing video:", e);
                                showStatus("Error displaying camera feed: " + e.message, STATUS_TYPE.ERROR);
                            });
                        }
                    }, 300);
                } catch (videoError) {
                    console.error("Error setting up video element:", videoError);
                    showStatus("Error setting up video: " + videoError.message, STATUS_TYPE.ERROR);
                }
            }

            updateState({ permissionStatus: PERMISSION_STATUS.GRANTED });
            showStatus("Camera access granted!", STATUS_TYPE.SUCCESS);

            // Initialize the code reader
            initializeCodeReader();

            // Detect available cameras
            try {
                const deviceList = await BrowserMultiFormatReader.listVideoInputDevices();

                if (deviceList && deviceList.length > 0) {
                    // Prefer back camera on mobile
                    let backCamera = deviceList.find(device =>
                        device.label.toLowerCase().includes('back') ||
                        device.label.toLowerCase().includes('rear') ||
                        device.label.toLowerCase().includes('environment')
                    );

                    if (!backCamera && deviceList.length > 1) {
                        // If no camera has "back" in name, use second camera which is typically back camera
                        backCamera = deviceList[1];
                    }

                    const newDeviceId = backCamera ? backCamera.deviceId : deviceList[0].deviceId;

                    updateState({
                        devices: deviceList,
                        selectedDeviceId: newDeviceId
                    });

                    // Wait before switching device to avoid racing conditions
                    setTimeout(() => {
                        if (videoRef.current) {
                            switchToDevice(newDeviceId);
                        }
                    }, 500);
                }
            } catch (deviceError) {
                console.error("Error listing devices:", deviceError);
                showStatus("Could not detect cameras: " + deviceError.message, STATUS_TYPE.WARNING);
            }

        } catch (error) {
            console.error("Camera permission error:", error);
            updateState({ permissionStatus: PERMISSION_STATUS.DENIED });
            showStatus("Camera access was denied. Please check browser settings.", STATUS_TYPE.ERROR);
        }
    }, [updateState, showStatus, cleanupCameraStream, initializeCodeReader]);

    // Helper function to switch to a specific camera device
    const switchToDevice = useCallback(async (deviceId) => {
        try {
            // Stop any existing stream
            cleanupCameraStream();

            // Create constraints with the selected device
            const constraints = {
                audio: false,
                video: {
                    deviceId: { exact: deviceId },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            // Attach the new stream to the video element
            if (videoRef.current) {
                videoRef.current.srcObject = stream;

                // Use a delay before playing to avoid race conditions
                setTimeout(() => {
                    if (videoRef.current) {
                        videoRef.current.play().catch(e => {
                            console.error("Error playing video after device switch:", e);
                            showStatus("Camera feed error. Try the Refresh Camera button.", STATUS_TYPE.ERROR);
                        });
                    }
                }, 300);
            } else {
                console.error("Video ref is null when switching device");
            }
        } catch (error) {
            showStatus("Failed to switch camera: " + error.message, STATUS_TYPE.ERROR);
        }
    }, [cleanupCameraStream, showStatus]);

    // Process the scanned QR code

    const handleApiCall = useCallback(async (barcodeValue, pkgName, projectNumber) => {
        updateState({ loading: true });
        showStatus("Processing QR code: " + barcodeValue + " for package: " + pkgName, STATUS_TYPE.INFO);

        try {
            // Ensure the package_name follows the correct format
            const formattedPackageName = `${projectNumber}_package_${packageNum}`;

            try {
                const checkResponse = await HttpService.checkPackage({ package_name: barcodeValue });
                const checkResult = checkResponse.data;

                // If document exists
                if (checkResult.exists === true) {
                    if (checkResult.wasUpdated === false) {
                        showStatus(`Package "${barcodeValue}" successfully marked as scanned!`, STATUS_TYPE.SUCCESS);
                    } else {
                        showStatus(`Package "${barcodeValue}" was already scanned!`, STATUS_TYPE.SUCCESS);
                    }

                    // dispatch(setLatestScan(JSON.parse(JSON.stringify(projectNumber))))

                    calculateCompletionPiece(projectNumber)
                    updateState({ lastScanned: barcodeValue });
                    sessionStorage.setItem('projectNo', JSON.stringify(projectNumber));

                    return {
                        success: true,
                        message: checkResult.wasUpdated ? "Package marked as scanned" : "Package already processed",
                        barcode: barcodeValue
                    };
                }
            } catch (checkError) {
                console.warn("Error checking package existence:", checkError);
            }

            // Step 2: If document doesn't exist, fetch data from ERP API
            const payload = {
                package_name: barcodeValue,
                barcode: barcodeValue,
                project_no: projectNumber
            };

            const response = await HttpService.sendDataToERP(payload);

            if (
                response.data.msg &&
                (response.data.msg.includes("No packages found for project") ||
                    response.data.msg.includes("Package not found"))
            ) {
                showStatus("Warning: Package Not Found", STATUS_TYPE.WARNING);
                return;
            }

            const responseData = response.data;

            // Validate ERP response
            if (!responseData.success) {
                throw new Error(responseData.error || "ERP API returned unsuccessful response");
            }

            // Step 3: Process and send scan data to storage
            try {
                // Send the data to scan storage service
                const scanResponse = await HttpService.storeScanPiece(responseData);

                // Show more detailed success message
                showStatus(
                    `Package "${formattedPackageName}" successfully scanned with ${responseData.matchedProducts?.length || 0} products!`,
                    STATUS_TYPE.SUCCESS
                );
            } catch (scanError) {
                showStatus("Warning: Package processed but scan data storage failed", STATUS_TYPE.WARNING);
            }

            dispatch(setLatestScan(JSON.parse(JSON.stringify(projectNumber))))
            let store = sessionStorage.setItem('projectNo', JSON.stringify(projectNo));

            calculateCompletionPiece(projectNo)
            updateState({ lastScanned: formattedPackageName });
            return responseData; // Return the response data for further processing if needed
        } catch (error) {
            showStatus("Failed to process package: " + error.message, STATUS_TYPE.ERROR);
            throw error; // Re-throw the error for handling at a higher level if needed
        } finally {
            updateState({ loading: false });
        }
    }, [packageNum, showStatus, updateState]);




    const calculateCompletionPiece = async (projectNumber) => {

        try {
            let res = await HttpService.calculateCompletionPiece(projectNumber);
            setCompletionCount(res.data)
        } catch (error) {
            showStatus("error", "Failed To Load Data")
        }
    }


    const processQrCode = useCallback(async (scannedValue) => {
        // Basic validation

        
        if (cameraLockedRef.current === true) {
         
            return;
        }
        
        // Lock the camera
        cameraLockedRef.current = true;
        
        if (!scannedValue || typeof scannedValue !== 'string') {
            showStatus("Invalid QR code detected", STATUS_TYPE.WARNING);
            return;
        }
    
        // Extract project and package info from QR code
        let extractedProjectNo = "";
        let extractedPackageNo = "";
    
        if (scannedValue.includes("_")) {
            // For formats like WF012567876_1, take first part as project number and second as package number
            const parts = scannedValue.split('_');
            extractedProjectNo = parts[0];
            // Last part as package number
            extractedPackageNo = parts[parts.length - 1];
        } else {
            // For example: If scanning just a number like "12345", treat as project number
            extractedProjectNo = scannedValue;
            extractedPackageNo = packageNum; // Use the current package number
        }
    
        // Update the state with the extracted values
        updateState({
            data: scannedValue,
            projectNo: extractedProjectNo || projectNo,
            packageNum: extractedPackageNo || packageNum
        });
    
        // Provide haptic feedback if available
        if (navigator.vibrate) {
            navigator.vibrate(200);
        }
    
        // Create the formatted package_name from the extracted values
        const formattedPackageName = `${extractedProjectNo}_package_${extractedPackageNo}`;
    
        // Process the scan with API call
        showStatus("Scanned: " + scannedValue, STATUS_TYPE.WARNING);
    
        try {
            await handleApiCall(formattedPackageName, formattedPackageName, extractedProjectNo);
          
           
            // After successful processing, update button states
            updateState({
                startButtonDisabled: true,
                stopButtonDisabled: true,
                continueButtonDisabled: false
            });
        } catch (error) {
            // Reset scanning buttons on error
            updateState({
                startButtonDisabled: false,
                stopButtonDisabled: true,
                continueButtonDisabled: true
            });
            
            // Keep camera locked on error - require manual restart
        }
    }, [packageNum, projectNo, updateState, showStatus, handleApiCall]);
    
    // Start scanning function
    const handleStartScan = useCallback(async () => {

        // Ensure we have camera permission
        if (permissionStatus !== PERMISSION_STATUS.GRANTED) {
            showStatus("Please grant camera permission first", STATUS_TYPE.WARNING);
            await requestCameraPermission();
            return;
        }

        // Make sure we have a working code reader
        if (!codeReader.current) {
            if (!initializeCodeReader()) {
                showStatus("Failed to initialize QR code scanner. Please refresh the page.", STATUS_TYPE.ERROR);
                return;
            }
        }

        updateState({
            data: "Scanning...",
            scanning: true,
            startButtonDisabled: true,
            stopButtonDisabled: false,
            continueButtonDisabled: true
        });

        showStatus("Scanner active - position QR code in view", STATUS_TYPE.INFO);

        try {
            // Check if video element is showing a stream
            if (videoRef.current && (!videoRef.current.srcObject || !videoRef.current.srcObject.active)) {
                await requestCameraPermission();

                // Wait a moment for camera to initialize
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // Double check video ref before starting scan
            if (!videoRef.current) {
                showStatus("Camera not ready. Please try again.", STATUS_TYPE.ERROR);
                updateState({
                    scanning: false,
                    startButtonDisabled: false,
                    stopButtonDisabled: true,
                    continueButtonDisabled: true
                });
                return;
            }

            try {
                await codeReader.current.decodeFromVideoElement(
                    videoRef.current,
                    async (result, err) => {
                        if (result) {
                            const scannedValue = result.getText();

                            // Stop scanning temporarily while processing
                            updateState({ scanning: false });
                            safelyResetCodeReader();
                          
  
                            let sessionProjectNoRaw = sessionStorage.getItem("projectNo") || "";
                            // Remove any leading and trailing quotes if present
                            const sessionProjectNo = sessionProjectNoRaw.replace(/^"(.*)"$/, '$1').trim();
                            
                            const scannedProjectNo = String(scannedValue.split('_')[0] || "").trim();
                            
                            
                            if (!sessionProjectNo) {
                              sessionStorage.setItem("projectNo", JSON.stringify(scannedProjectNo));
                              showStatus(`Started scanning project: ${scannedProjectNo}`, STATUS_TYPE.SUCCESS);
                            } else if (scannedProjectNo !== sessionProjectNo) {
                              showStatus(
                                "Scanned QR code is from another project. Please stop scanning of current project and click on Start Scanning a new project",
                                STATUS_TYPE.WARNING
                              );
                              setTimeout(() => {
                                handleContinueScan();
                              }, 2000);
                              return;
                            }
                            

                            // Process the scanned QR code
                            await processQrCode(scannedValue);
                        }

                        if (err && !(err.name === "NotFoundException")) {
                            console.warn("Scan error:", err);
                        }
                    }
                ).catch(error => {
                    console.error("Decoder error:", error);
                    updateState({
                        scanning: false,
                        startButtonDisabled: false,
                        stopButtonDisabled: true,
                        continueButtonDisabled: true
                    });
                    showStatus("Scanner error: " + error.message, STATUS_TYPE.ERROR);
                });
            } catch (decodeError) {
                console.error("Failed to start decoder:", decodeError);
                updateState({
                    scanning: false,
                    startButtonDisabled: false,
                    stopButtonDisabled: true,
                    continueButtonDisabled: true
                });
                showStatus("Failed to start QR code scanner. Please try again.", STATUS_TYPE.ERROR);

                // Reinitialize code reader for next attempt
                codeReader.current = null;
                initializeCodeReader();
            }

        } catch (error) {
            console.error("Error in scan process:", error);
            updateState({
                scanning: false,
                startButtonDisabled: false,
                stopButtonDisabled: true,
                continueButtonDisabled: true
            });
            showStatus("An error occurred: " + error.message, STATUS_TYPE.ERROR);
        }
    }, [
        permissionStatus,
        showStatus,
        requestCameraPermission,
        initializeCodeReader,
        updateState,
        safelyResetCodeReader,
        processQrCode
    ]);

    // API call to handle scanned packages

    // Handler for manual package submission
    const handlePackageSubmit = useCallback(() => {
        if (!package_name.trim()) {
            showStatus("Please enter a package number", STATUS_TYPE.WARNING);
            return;
        }

        updateState({ data: "Manual package: " + package_name });

        // Process with the package name as QR code - ensure it uses the correct format
        const formattedPackageName = `${projectNo}_package_${packageNum}`;

        handleApiCall(formattedPackageName, formattedPackageName, projectNo);
    }, [package_name, projectNo, packageNum, updateState, showStatus, handleApiCall]);

    // Stop scanning function
    const openConfirmDialog = (message, confirmCallback) => {
        setDialogMessage(message);
        setOnDialogConfirm(() => confirmCallback);
        setDialogOpen(true);
    };

    // Handle dialog close without confirmation
    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    // Handle dialog confirmation
    const handleDialogConfirm = () => {
        setDialogOpen(false);
        onDialogConfirm();
        // ✅ Clear projectNo from sessionStorage
        sessionStorage.removeItem("projectNo");
        updateState({
            scanning: false,
            startButtonDisabled: false,
            stopButtonDisabled: true,
            continueButtonDisabled: true
          });
    };

    // Modified handleStopScan function

    // This is the separate function that runs when user confirms
    const handleConfirmedStop = async () => {
        let data = {
            status: "Under Execution",
            uniqueLeadNumber: projectNo,
            materialReceivedDate: new Date()
        }
        cameraLockedRef.current = false;
        await HttpService.changeStateToUnderExecution(data)
        updateState({
            startButtonDisabled: false,
            stopButtonDisabled: true,
            continueButtonDisabled: true,
            scanning: false,
           
        });

    }
    // Continue to next scan
    const handleContinueScan = useCallback(() => {
        cameraLockedRef.current = false;
        updateState({
            startButtonDisabled: true,
            stopButtonDisabled: false,
            continueButtonDisabled: false,
            scanning: true,
            data: "Waiting for scan..."
        });
        
    
        showStatus("Ready to scan next QR code", STATUS_TYPE.INFO);
    }, [updateState, showStatus]);
    
    const handleStopScan = useCallback(() => {
        const confirmMessage = `P.No - ${projectNo || "N/A"}, Out of ${completionCount.counts?.total || 0} packages, ${completionCount.counts?.scanned || 0} packages are scanned`;
      
        openConfirmDialog(confirmMessage, () => {
          updateState({
            scanning: false,
            startButtonDisabled: true,
            stopButtonDisabled: true,
            continueButtonDisabled: false
          });
      
          showStatus(confirmMessage, STATUS_TYPE.INFO);
      
          safelyResetCodeReader();
      
       
      
          handleConfirmedStop();
        });
      }, [projectNo, completionCount, updateState, showStatus, safelyResetCodeReader, handleConfirmedStop]);
      

    // Handle input changes
    const handleInputChange = useCallback((field, value) => {
        updateState({ [field]: value });
    }, [updateState]);

    // Effect to initialize code reader when component mounts
    useEffect(() => {
        initializeCodeReader();

        // Clean up resources when component unmounts
        return () => {
            // Clean up status timeout
            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
            }

            // Clean up code reader
            safelyResetCodeReader();

            // Clean up camera stream
            cleanupCameraStream();
        };
    }, [initializeCodeReader, safelyResetCodeReader, cleanupCameraStream]);

    // Effect to request camera permission when switching to automated mode
    useEffect(() => {
        if (scanMode === SCAN_MODE.AUTOMATED && permissionStatus === PERMISSION_STATUS.UNKNOWN) {
            // Add a slight delay to ensure DOM is ready
            const timerId = setTimeout(() => {
                requestCameraPermission();
            }, 300);

            return () => clearTimeout(timerId);
        }
    }, [scanMode, permissionStatus, requestCameraPermission]);

    // Handle device selection changes
    useEffect(() => {
        if (selectedDeviceId && permissionStatus === PERMISSION_STATUS.GRANTED) {
            switchToDevice(selectedDeviceId);
        }
    }, [selectedDeviceId, permissionStatus, switchToDevice]);

    // This useEffect will periodically check if the video is actually playing
    // and attempt to restart it if it's not
    useEffect(() => {
        let videoCheckInterval;

        if (scanMode === SCAN_MODE.AUTOMATED && permissionStatus === PERMISSION_STATUS.GRANTED) {
            videoCheckInterval = setInterval(() => {
                if (videoRef.current) {
                    const isPlaying = !!(videoRef.current.currentTime > 0 &&
                        !videoRef.current.paused &&
                        !videoRef.current.ended &&
                        videoRef.current.readyState > 2);

                    // If video element exists but isn't playing, try to restart
                    if (!isPlaying && videoRef.current.srcObject) {
                        videoRef.current.play().catch(e => {
                            console.error("Failed to restart video:", e);

                            // If play fails, try to re-request camera permission
                            if (e.name === "NotAllowedError" || e.name === "NotReadableError") {
                                requestCameraPermission();
                            }
                        });
                    }
                }
            }, 3000); // Check every 3 seconds
        }

        return () => {
            if (videoCheckInterval) {
                clearInterval(videoCheckInterval);
            }
        };
    }, [scanMode, permissionStatus, requestCameraPermission]);

    // Render the status bar
    const renderStatusBar = () => (
        statusMessage ? (
          <Box sx={styles.statusBar(statusType)} mb={3} p={2} borderRadius={1}>
            <Typography variant="body2"><strong>{statusMessage}</strong></Typography>
          </Box>
        ) : null
      );

    const renderPieceCompletion = () => {
        // Check if data exists to avoid errors
        if (!completionCount) return null;

        return (
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3} align="center">
                                Project: {completionCount.projectNumber}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell align="center">Scanned</TableCell>
                            <TableCell align="center">Pending</TableCell>
                            <TableCell align="center">Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell align="center">{completionCount.counts?.scanned || 0}</TableCell>
                            <TableCell align="center">{completionCount.counts?.pending || 0}</TableCell>
                            <TableCell align="center">{completionCount.counts?.total || 0}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }
    // Render the manual entry section
    const renderManualEntry = () => (
        scanMode === SCAN_MODE.MANUAL && (
            <Box sx={styles.sectionContainer} mb={3} p={2}>
                <Typography variant="h6" gutterBottom align="center">
                    Manual Entry
                </Typography>

                <div style={styles.flexColumn}>
                    <div>
                        <form>
                            {/* <input type="text" name="fakeusernameremembered" style={{ display: 'none' }} /> */}

                            <TextField
                                id="packageNum"
                                name="packageNum"
                                label="Package Number"
                                type="text"
                                autoComplete="new-password"
                                onChange={(e) => handleInputChange('packageNum', e.target.value)}
                                disabled={loading}
                                placeholder="Enter Package Number"
                                variant="outlined"
                                fullWidth
                            // helperText={`Full Package Name: ${package_name}`}
                            />
                        </form>

                    </div>

            <div style={{ marginTop: '8px', display: 'flex', gap: '10px' }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handlePackageSubmit}
                    disabled={loading || !package_name.trim()}
                    style={{ flex: 1 }}
                >
                    {loading ? (
                        <React.Fragment>
                            Processing... <CircularProgress size={20} sx={{ marginLeft: 1, color: 'white' }} />
                        </React.Fragment>
                    ) : (
                        'Submit Package'
                    )}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleStopScan}
                    disabled={completionCount.counts?.scanned === 0}
                    style={{ flex: 1 }}
                >
                    Stop Scanning
                </Button>
               </div>
                </div>
            </Box>
        )
    );

    // Render the camera permission section
    // Render the camera permission section
    const renderCameraPermission = () => (
        permissionStatus !== PERMISSION_STATUS.GRANTED && (
            <Box sx={styles.permissionSection} mb={3} pt={2} pb={2}>
                <Typography variant="subtitle1" gutterBottom>
                    Camera Status: {
                        permissionStatus === PERMISSION_STATUS.UNKNOWN ? "Not requested" :
                            permissionStatus === PERMISSION_STATUS.REQUESTING ? "Requesting..." :
                                "Access denied"
                    }
                </Typography>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={requestCameraPermission}
                    disabled={permissionStatus === PERMISSION_STATUS.REQUESTING}
                    size="large"
                    sx={{ marginTop: 1 }}
                >
                    {permissionStatus === PERMISSION_STATUS.REQUESTING ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        "Allow Camera Access"
                    )}
                </Button>
            </Box>
        )
    );

    // Render the camera selection dropdown
    const renderCameraSelector = () => (
        devices.length > 1 && (
            <Box mb={2}>
                <TextField
                    select
                    id="cameraSelect"
                    label="Select Camera"
                    value={selectedDeviceId}
                    onChange={(e) => handleInputChange('selectedDeviceId', e.target.value)}
                    disabled={scanning || loading}
                    variant="outlined"
                    fullWidth
                    SelectProps={{
                        native: true,
                    }}
                >
                    {devices.map(device => (
                        <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${devices.indexOf(device) + 1}`}
                        </option>
                    ))}
                </TextField>
            </Box>
        )
    );



    // Render scan control buttons
    const renderScanControls = () => (
        <Box sx={styles.scanControls}>
            <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleStartScan}
                disabled={startButtonDisabled || loading}
                sx={styles.scanButton}
            >
                Start
            </Button>

            <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleContinueScan}
                disabled={continueButtonDisabled || loading}
                sx={styles.scanButton}
            >
                Continue
            </Button>

            <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={handleStopScan}
                disabled={continueButtonDisabled || loading ||  completionCount.counts?.scanned === 0}
                sx={styles.scanButton}
            >
                Stop
            </Button>
        </Box>
    );

    // Render camera view with scanner UI
    const renderCameraView = () => (
        <Box sx={styles.videoContainer} position="relative">
            <video
                ref={videoRef}
                style={styles.video}
                muted
                autoPlay
                playsInline
            />

            {/* Scanner UI elements */}
            {scanning && (
                <>
                    {/* Scan instruction */}
                    <Box sx={styles.scanInstruction}>
                        <Typography variant="body2">
                            Position QR code in view
                        </Typography>
                    </Box>

                    {/* Target area for QR code */}
                    <Box sx={styles.targetArea} />

                    {/* QR code corner markers */}
                    <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        sx={{
                            transform: "translate(-50%, -50%)",
                            width: "70%",
                            height: "70%",
                            maxHeight: "280px",
                            maxWidth: "280px",
                            pointerEvents: "none",
                        }}
                    >
                        {/* Top Left Corner */}
                        <div style={{ ...styles.cornerMarker, ...styles.topLeftCorner }} />

                        {/* Top Right Corner */}
                        <div style={{ ...styles.cornerMarker, ...styles.topRightCorner }} />

                        {/* Bottom Left Corner */}
                        <div style={{ ...styles.cornerMarker, ...styles.bottomLeftCorner }} />

                        {/* Bottom Right Corner */}
                        <div style={{ ...styles.cornerMarker, ...styles.bottomRightCorner }} />
                    </Box>

                    {/* Animated scan line */}
                    <Box sx={styles.scanLine} />
                </>
            )}
        </Box>
    );

    // Render the camera scanner section
    const renderCameraScanner = () => (
        scanMode === SCAN_MODE.AUTOMATED && (
            <Box mb={3}>
                <Typography variant="h6" gutterBottom align="center">
                    Camera Scanner
                </Typography>

                {/* Camera permission request */}
                {renderCameraPermission()}

                {permissionStatus === PERMISSION_STATUS.GRANTED && (
                    <>
                        {/* Camera selection dropdown */}
                        {renderCameraSelector()}

                        {/* Package input */}

                        {/* Scan controls */}
                        {renderScanControls()}

                        {/* Debug button for refreshing camera */}
                        <Box mb={2} textAlign="center">
                            <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                onClick={requestCameraPermission}
                                disabled={loading}
                            >
                                Refresh Camera
                            </Button>
                        </Box>

                        {/* Camera view */}
                        {renderCameraView()}
                    </>
                )}
            </Box>
        )
    );
    return (
        <Paper sx={styles.container}>
            <Typography variant="h5" gutterBottom align="center">
                QR Code Scanner
            </Typography>

            {/* Status message bar */}
           

            {/* Scan Mode Toggle */}
            {userDetails?.roles?.find(role =>
                role.name === ROLENAME.Execution_Manager ||
                role.name === ROLENAME.ProjectManager ||
                role.name === ROLENAME.SiteInspector
            ) && (
                    <Box mb={3} display="flex" justifyContent="center" alignItems="center">
                        <Typography variant="body1" sx={{ marginRight: 1 }}>Manual</Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={scanMode === SCAN_MODE.AUTOMATED}
                                    onChange={handleToggleMode}
                                    color="primary"
                                />
                            }
                            label=""
                        />
                        <Typography variant="body1" sx={{ marginLeft: 1 }}>Automated</Typography>
                    </Box>
                )}

            {/* Project Selection (Available in both modes) */}
            {state.scanMode === SCAN_MODE.MANUAL && (
                <Box mb={3}>
                                <Autocomplete
                    id="project-autocomplete"
                    options={projectOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.uniqueLeadNumber || ''}
                    value={projectNo || ''}  // Directly use the selected string value
                    freeSolo  // Allow free text input
                    inputValue={projectNo}
                    onInputChange={(event, newInputValue) => {
                        handleInputChange('projectNo', newInputValue);  // Always update with what user types
                    }}
                    onChange={(event, newValue) => {
                        if (typeof newValue === 'string') {
                            handleInputChange('projectNo', newValue);  // Free text
                        } else if (newValue?.uniqueLeadNumber) {
                            handleInputChange('projectNo', newValue.uniqueLeadNumber);  // From options
                        }
                    }}
                    disabled={loading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Project"
                            variant="outlined"
                            fullWidth
                            helperText={`Selected Project Code: ${projectNo}`}
                        />
                    )}
                />

                </Box>

            )}

            {state.scanMode === SCAN_MODE.MANUAL && (
                renderManualEntry()
            )}

            {/* Camera Scanner Section */}
            {renderCameraScanner()}
            {renderStatusBar()}

            {/* Results display */}
            {/* {renderScanResults()} */}

            {renderPieceCompletion()}

            {/* Animation styles - Using keyframes */}
            <style jsx>{keyframes}</style>
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >

                <DialogContent>
                    <DialogContentText id="alert-dialog-description" style={{ whiteSpace: 'nowrap' }}>
                        {dialogMessage}
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 2, textAlign: 'center', fontWeight: 'bold' }}>
                        Do you want to Stop?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', padding: '16px' }}>
                    <Button
                        onClick={handleDialogConfirm}
                        variant="contained"
                        autoFocus
                        sx={{
                            mx: 1,
                            backgroundColor: '#8BC34A', // light green
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#7CB342' // slightly darker green on hover
                            }
                        }}
                    >
                        Yes
                    </Button>
                    <Button
                        onClick={handleDialogClose}
                        variant="contained"
                        sx={{
                            mx: 1,
                            backgroundColor: '#FF8A80', // light red
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#FF5252' // slightly darker red on hover
                            }
                        }}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default connect(null, {  })(ScanPiece)